<template>
  <section class="[ max-w-7xl m-auto min-h-full flex flex-col gap-6 ]">

    <!-- ============ LIST VIEW ============ -->
    <template v-if="viewMode === 'list'">
      <div class="[ flex items-center justify-between ]">
        <h2 class="[ text-xl font-semibold ]">Rules</h2>
        <Button label="New Rule" @click="openCreate">
          <template #icon>
            <JBIcon name="add" />
          </template>
        </Button>
      </div>

      <div
        v-if="loading"
        class="[ flex-1 flex flex-col items-center justify-center ]"
      >
        <JBLoadingSpinner message="Loading rules" />
      </div>

      <DataTable v-else :value="items" striped-rows>
        <Column field="name" header="Name" sortable>
          <template #body="{ data }">
            <div class="[ flex flex-col ]">
              <span class="[ font-medium ]">{{ data.name }}</span>
              <span class="[ text-xs text-gray-dark ]">{{ data.ruleType }}</span>
            </div>
          </template>
        </Column>
        <Column field="category" header="Category" sortable>
          <template #body="{ data }">
            <Tag :value="data.category" severity="info" />
          </template>
        </Column>
        <Column field="maxPoints" header="Pts" sortable>
          <template #body="{ data }">
            {{ data.maxPoints }}
          </template>
        </Column>
        <Column field="scoringMethod" header="Method" sortable>
          <template #body="{ data }">
            <Tag :value="data.scoringMethod" :severity="methodSeverity(data.scoringMethod)" />
          </template>
        </Column>
        <Column field="difficulty" header="Difficulty" sortable>
          <template #body="{ data }">
            <span
              class="[ inline-block text-xs font-medium px-2 py-0.5 rounded-full ]"
              :class="difficultyClass(data.difficulty)"
            >
              {{ data.difficulty }}
            </span>
          </template>
        </Column>
        <Column field="status" header="Status" sortable>
          <template #body="{ data }">
            <span
              class="[ inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full ]"
              :class="statusClass(data.status)"
            >
              <span
                class="[ inline-block w-1.5 h-1.5 rounded-full ]"
                :class="statusDotClass(data.status)"
              />
              {{ data.status }}
            </span>
          </template>
        </Column>
        <Column header="" style="width: 7rem">
          <template #body="{ data }">
            <div class="[ flex gap-2 items-center ]">
              <a
                class="[ text-sm text-primary hover:underline cursor-pointer ]"
                @click="openEdit(data)"
              >
                Edit
              </a>
              <PhX
                class="[ text-red-500 hover:text-red-700 cursor-pointer ]"
                :size="16"
                weight="bold"
                @click="onDelete(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </template>

    <!-- ============ EDIT VIEW — Chat + Form side-by-side ============ -->
    <template v-else>
      <div class="[ flex items-center justify-between ]">
        <button
          class="[ flex items-center gap-1 text-sm text-primary hover:underline ]"
          @click="backToList"
        >
          <JBIcon name="arrow-left" />
          Back to Rules
        </button>
        <div class="[ flex items-center gap-3 ]">
          <h2 class="[ text-xl font-semibold ]">
            {{ editingItem ? "Edit Rule" : "New Rule" }}
          </h2>
          <Select
            v-model="formData.status"
            :options="statusOptions"
            option-label="label"
            option-value="value"
            class="[ w-32 ]"
          />
        </div>
        <div class="[ flex items-center gap-2 ]">
          <Button
            label="Cancel"
            severity="secondary"
            text
            @click="backToList"
          />
          <Button
            label="Save"
            :disabled="!formData.name"
            @click="onSave"
          />
        </div>
      </div>

      <div class="[ flex gap-6 flex-1 min-h-0 ]">
        <!-- Chat panel (left, ~40%) -->
        <div class="[ w-2/5 flex flex-col border rounded-lg overflow-hidden ]">
          <!-- Chat header -->
          <div class="[ flex items-center justify-between px-4 py-3 border-b bg-gray-50 ]">
            <span class="[ text-sm font-medium ]">Rule Assistant</span>
            <span
              class="[ flex items-center gap-1.5 text-xs ]"
              :class="isLive ? 'text-green-600' : 'text-gray-400'"
            >
              <span
                class="[ inline-block w-2 h-2 rounded-full ]"
                :class="isLive ? 'bg-green-500' : 'bg-gray-300'"
              />
              {{ isLive ? "Live" : "Demo mode" }}
            </span>
          </div>

          <!-- Messages -->
          <div ref="chatContainer" class="[ flex-1 overflow-y-auto p-4 flex flex-col gap-3 ]">
            <div
              v-for="msg in messages"
              :key="msg.id"
              class="[ max-w-[85%] text-sm rounded-lg px-3 py-2 ]"
              :class="
                msg.role === 'user'
                  ? '[ self-end bg-primary text-white ]'
                  : '[ self-start bg-gray-100 text-gray-800 ]'
              "
            >
              <div class="[ whitespace-pre-wrap ]" v-html="formatMessage(msg.content)" />
            </div>
            <div
              v-if="isTyping"
              class="[ self-start bg-gray-100 text-gray-400 text-sm rounded-lg px-3 py-2 ]"
            >
              Typing…
            </div>
          </div>

          <!-- Chat input -->
          <div class="[ border-t p-3 flex items-end gap-2 ]">
            <Textarea
              v-model="chatInput"
              placeholder="Describe the rule you want to create…"
              auto-resize
              rows="1"
              class="[ flex-1 ]"
              @keydown.enter.exact.prevent="handleSendMessage"
            />
            <button
              class="[ flex items-center justify-center w-9 h-9 rounded-full text-primary hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed ]"
              :disabled="!chatInput.trim() || isTyping"
              @click="handleSendMessage"
            >
              <PhPaperPlaneTilt :size="18" weight="fill" />
            </button>
          </div>
        </div>

        <!-- Form panel (right, ~60%) -->
        <div class="[ w-3/5 overflow-y-auto flex flex-col gap-4 ]">

          <!-- Validation Checklist -->
          <div
            class="[ border rounded-lg p-4 ]"
            :class="validationScore === validationChecks.length ? 'border-green-300' : 'border-amber-300'"
          >
            <div class="[ flex items-center justify-between mb-3 ]">
              <h3 class="[ text-sm font-semibold ]">Validation Checklist</h3>
              <span
                class="[ text-xs font-bold ]"
                :class="validationScore === validationChecks.length ? 'text-green-600' : 'text-amber-600'"
              >
                {{ validationScore }}/{{ validationChecks.length }}
              </span>
            </div>
            <div class="[ flex flex-col gap-1.5 ]">
              <div
                v-for="check in validationChecks"
                :key="check.label"
                class="[ flex items-center gap-2 text-sm ]"
              >
                <span
                  class="[ inline-block w-2 h-2 rounded-full flex-shrink-0 ]"
                  :class="check.passed ? 'bg-green-500' : 'bg-red-500'"
                />
                <span :class="check.passed ? 'text-gray-600' : 'font-medium'">
                  {{ check.label }}
                </span>
              </div>
            </div>
          </div>

          <!-- Rule Metadata -->
          <div class="[ border rounded-lg ]">
            <div class="[ px-4 py-3 border-b ]">
              <h3 class="[ text-sm font-semibold ]">Rule Metadata</h3>
            </div>
            <div class="[ p-4 ]">
              <div class="[ grid grid-cols-2 gap-x-4 gap-y-3 ]">
                <div class="[ flex flex-col gap-1 ]">
                  <label class="[ text-xs font-medium text-gray-dark ]" for="rule-name">Name</label>
                  <InputText
                    id="rule-name"
                    v-model="formData.name"
                    placeholder="e.g. Agent Greeting"
                  />
                </div>
                <div class="[ flex flex-col gap-1 ]">
                  <label class="[ text-xs font-medium text-gray-dark ]" for="rule-category">Category</label>
                  <Select
                    id="rule-category"
                    v-model="formData.category"
                    :options="categoryOptions"
                    placeholder="Select category"
                  />
                </div>
                <div class="[ flex flex-col gap-1 ]">
                  <label class="[ text-xs font-medium text-gray-dark ]" for="rule-max-points">Max Points</label>
                  <InputNumber
                    id="rule-max-points"
                    v-model="formData.maxPoints"
                    :min="1"
                    :max="10"
                    show-buttons
                  />
                </div>
                <div class="[ flex flex-col gap-1 ]">
                  <label class="[ text-xs font-medium text-gray-dark ]" for="rule-model-tier">Model Tier</label>
                  <Select
                    id="rule-model-tier"
                    v-model="formData.modelTier"
                    :options="modelTierOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="Select model tier"
                  />
                </div>
                <div class="[ flex flex-col gap-1 ]">
                  <label class="[ text-xs font-medium text-gray-dark ]" for="rule-type">Rule Type</label>
                  <Select
                    id="rule-type"
                    v-model="formData.ruleType"
                    :options="ruleTypeOptions"
                    placeholder="Select a type"
                  />
                </div>
                <div class="[ flex flex-col gap-1 ]">
                  <label class="[ text-xs font-medium text-gray-dark ]" for="rule-difficulty">Difficulty</label>
                  <Select
                    id="rule-difficulty"
                    v-model="formData.difficulty"
                    :options="difficultyOptions"
                    placeholder="Select difficulty"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Statement -->
          <div class="[ border rounded-lg ]">
            <div class="[ px-4 py-3 border-b flex items-center justify-between ]">
              <h3 class="[ text-sm font-semibold ]">Statement</h3>
              <span class="[ text-xs text-gray-dark ]">Injected into LLM prompts</span>
            </div>
            <div class="[ p-4 ]">
              <Textarea
                v-model="formData.statement"
                rows="3"
                class="[ w-full ]"
                placeholder="Plain-English description of what the agent must do…"
              />
            </div>
          </div>

          <!-- Additional Instructions -->
          <div class="[ border rounded-lg ]">
            <div class="[ px-4 py-3 border-b ]">
              <h3 class="[ text-sm font-semibold ]">Additional Instructions</h3>
            </div>
            <div class="[ p-4 ]">
              <Textarea
                v-model="formData.additionalInstructions"
                rows="2"
                class="[ w-full ]"
                placeholder="Key phrases, timing constraints, edge-case guidance for the evaluator…"
              />
            </div>
          </div>

          <!-- Score Guide -->
          <div class="[ border rounded-lg ]">
            <div class="[ px-4 py-3 border-b flex items-center justify-between ]">
              <h3 class="[ text-sm font-semibold ]">Score Guide</h3>
              <div class="[ flex items-center gap-2 ]">
                <Tag :value="`${formData.scoringTiers.length}-tier`" severity="info" />
                <Button
                  v-if="!hasPartialTier"
                  label="Add Partial"
                  severity="secondary"
                  text
                  size="small"
                  @click="addPartialTier"
                >
                  <template #icon>
                    <JBIcon name="add" />
                  </template>
                </Button>
              </div>
            </div>
            <div class="[ overflow-hidden ]">
              <table class="[ w-full text-sm ]">
                <thead>
                  <tr class="[ border-b ]">
                    <th class="[ text-left text-xs uppercase tracking-wide font-semibold text-gray-dark px-4 py-2 ]">Outcome</th>
                    <th class="[ text-left text-xs uppercase tracking-wide font-semibold text-gray-dark px-4 py-2 w-20 ]">Pts</th>
                    <th class="[ text-left text-xs uppercase tracking-wide font-semibold text-gray-dark px-4 py-2 ]">Criteria</th>
                    <th class="[ w-10 ]" />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(tier, index) in formData.scoringTiers"
                    :key="tier.id"
                    :class="tierRowClass(tier.outcome)"
                  >
                    <td class="[ px-4 py-3 font-medium align-middle ]">{{ tier.outcome }}</td>
                    <td class="[ px-4 py-3 align-middle w-20 ]">
                      <input
                        v-if="tier.outcome === 'Partial'"
                        type="number"
                        :value="tier.points"
                        :min="1"
                        :max="formData.maxPoints - 1"
                        class="[ w-14 h-8 text-center border rounded text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ]"
                        @input="tier.points = clampPartialPoints(Number(($event.target as HTMLInputElement).value))"
                      />
                      <span v-else>{{ tier.points }}</span>
                    </td>
                    <td class="[ px-4 py-3 ]">
                      <InputText
                        v-model="tier.description"
                        placeholder="Criteria for this outcome"
                        class="[ w-full ]"
                      />
                    </td>
                    <td class="[ px-4 py-3 text-center ]">
                      <PhX
                        v-if="tier.outcome === 'Partial'"
                        class="[ text-red-500 hover:text-red-700 cursor-pointer ]"
                        :size="14"
                        weight="bold"
                        @click="removePartialTier(index)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Examples -->
          <div class="[ border rounded-lg ]">
            <div class="[ px-4 py-3 border-b flex items-center justify-between ]">
              <h3 class="[ text-sm font-semibold ]">Examples</h3>
              <div class="[ flex items-center gap-3 ]">
                <!-- Coverage indicator -->
                <div class="[ flex items-center gap-1.5 ]">
                  <span class="[ text-xs text-gray-dark ]">Coverage:</span>
                  <div class="[ flex gap-0.5 ]">
                    <span
                      v-for="tier in formData.scoringTiers"
                      :key="'cov-' + tier.id"
                      class="[ inline-block w-5 h-1.5 rounded-full ]"
                      :class="exampleCoversTier(tier.outcome) ? 'bg-green-500' : 'bg-gray-300'"
                    />
                  </div>
                </div>
                <Button
                  label="Add Example"
                  severity="secondary"
                  text
                  size="small"
                  @click="addExample"
                >
                  <template #icon>
                    <JBIcon name="add" />
                  </template>
                </Button>
              </div>
            </div>
            <div class="[ p-4 flex flex-col gap-3 ]">
              <p
                v-if="formData.examples.length === 0"
                class="[ text-sm text-gray-dark italic ]"
              >
                No examples added yet. Examples help calibrate the LLM evaluator via few-shot prompting.
              </p>

              <div
                v-for="(example, index) in formData.examples"
                :key="example.id"
                class="[ border rounded-lg p-4 flex flex-col gap-3 ]"
                :class="exampleBorderClass(example.expectedOutcome)"
              >
                <div class="[ flex items-center justify-between ]">
                  <div class="[ flex items-center gap-2 ]">
                    <Tag
                      v-if="example.expectedOutcome"
                      :value="`${example.expectedOutcome} — ${tierPointsForOutcome(example.expectedOutcome)} pts`"
                      :severity="outcomeSeverity(example.expectedOutcome)"
                    />
                    <span class="[ text-xs text-gray-dark ]">Example {{ index + 1 }}</span>
                  </div>
                  <PhX
                    class="[ text-red-500 hover:text-red-700 cursor-pointer ]"
                    :size="14"
                    weight="bold"
                    @click="removeExample(index)"
                  />
                </div>
                <div class="[ flex flex-col gap-3 ]">
                  <div class="[ flex flex-col gap-1 ]">
                    <label class="[ text-xs font-medium text-gray-dark ]">Transcript Snippet</label>
                    <Textarea
                      v-model="example.transcript"
                      rows="3"
                      placeholder="Synthetic transcript snippet…"
                    />
                  </div>

                  <div class="[ grid grid-cols-[1fr_2fr] gap-3 ]">
                    <div class="[ flex flex-col gap-1 ]">
                      <label class="[ text-xs font-medium text-gray-dark ]">Expected Outcome</label>
                      <Select
                        v-model="example.expectedOutcome"
                        :options="tierLabelOptions"
                        placeholder="Select outcome"
                      />
                    </div>
                    <div class="[ flex flex-col gap-1 ]">
                      <label class="[ text-xs font-medium text-gray-dark ]">Annotation</label>
                      <InputText
                        v-model="example.explanation"
                        placeholder="Why this example receives this outcome…"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Missing tier prompts -->
              <div
                v-for="missing in missingExampleTiers"
                :key="'missing-' + missing"
                class="[ border-2 border-dashed border-amber-400 rounded-lg p-4 text-center bg-amber-50 ]"
              >
                <span class="[ text-sm font-medium text-amber-800 ]">
                  Missing: {{ missing }} ({{ tierPointsForOutcome(missing) }} pts) example
                </span>
                <div class="[ mt-2 ]">
                  <Button
                    label="Add Example"
                    size="small"
                    severity="secondary"
                    @click="addExampleForOutcome(missing)"
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from "vue";
import {
  Button,
  Column,
  DataTable,
  InputText,
  InputNumber,
  Select,
  Tag,
  Textarea,
  useConfirm,
} from "primevue";
import { useMockCrud } from "@/mocks/composables/useMockApi";
import { useRuleChat, type RuleFormPatch } from "@/mocks/composables/useRuleChat";
import {
  generateRules,
  type MockRule,
  type MockRuleTaxonomy,
  type MockRuleCategory,
  type MockDifficulty,
  type MockModelTier,
  type MockRuleStatus,
  type MockScoringOutcome,
  type MockScoringMethod,
} from "@/mocks/mock-data/rules";
import { PhX, PhPaperPlaneTilt } from "@phosphor-icons/vue";
import JBLoadingSpinner from "@/components/ui/JBLoadingSpinner.vue";
import { faker } from "@faker-js/faker";

definePage({
  alias: "/v3/rules",
  meta: {
    title: "Rule Creation",
  },
});

// --- List state ---
const { items, loading, add, update, remove } = useMockCrud(generateRules(12));
const confirm = useConfirm();

// --- View mode ---
const viewMode = ref<"list" | "edit">("list");
const editingItem = ref<MockRule | null>(null);

// --- Chat ---
const { messages, sendMessage, isTyping, isLive, resetChat, lastFormPatch } = useRuleChat();
const chatInput = ref("");
const chatContainer = ref<HTMLElement | null>(null);

// --- Form options ---
const ruleTypeOptions: MockRuleTaxonomy[] = [
  "Presence-and-Timing",
  "Verbatim Compliance",
  "Permission and Consent",
  "Information Verification",
  "Quality-of-Explanation",
  "Inquiry Completeness",
];
const categoryOptions: MockRuleCategory[] = ["Compliance", "Quality", "Process"];
const difficultyOptions: MockDifficulty[] = ["Easy", "Medium", "Hard"];
const statusOptions: { label: string; value: MockRuleStatus }[] = [
  { label: "Draft", value: "draft" },
  { label: "Active", value: "active" },
  { label: "Archived", value: "archived" },
];
const modelTierOptions: { label: string; value: MockModelTier }[] = [
  { label: "Standard (fast)", value: "Standard" },
  { label: "Thorough (slower)", value: "Thorough" },
];
function emptyForm(): MockRule {
  return {
    id: faker.string.uuid(),
    name: "",
    statement: "",
    category: "Compliance",
    ruleType: "Presence-and-Timing",
    difficulty: "Medium",
    modelTier: "Standard",
    maxPoints: 5,
    scoringTiers: [
      { id: faker.string.uuid(), outcome: "Pass", points: 5, description: "Fully meets the requirement" },
      { id: faker.string.uuid(), outcome: "Fail", points: 0, description: "Does not meet the requirement" },
    ],
    examples: [],
    additionalInstructions: "",
    scoringMethod: "Hybrid",
    status: "draft" as MockRuleStatus,
    dateCreated: new Date(),
    dateUpdated: new Date(),
  };
}

const formData = ref<MockRule>(emptyForm());

const tierLabelOptions = computed(() =>
  formData.value.scoringTiers.map((t) => t.outcome),
);

const hasPartialTier = computed(() =>
  formData.value.scoringTiers.some((t) => t.outcome === "Partial"),
);

// --- Validation checklist ---
const validationChecks = computed(() => [
  { label: "Statement populated", passed: formData.value.statement.trim().length > 0 },
  { label: "No embedded point values in statement", passed: !/\b\d+\s*(pts?|points?)\b/i.test(formData.value.statement) },
  { label: "Pass/Fail tiers defined", passed: formData.value.scoringTiers.some((t) => t.outcome === "Pass") && formData.value.scoringTiers.some((t) => t.outcome === "Fail") },
  { label: "Category and max points set", passed: !!formData.value.category && formData.value.maxPoints > 0 },
  { label: "All tier criteria filled", passed: formData.value.scoringTiers.every((t) => t.description.trim().length > 0) },
  { label: "At least one example per tier", passed: formData.value.scoringTiers.every((t) => formData.value.examples.some((e) => e.expectedOutcome === t.outcome)) },
]);

const validationScore = computed(() =>
  validationChecks.value.filter((c) => c.passed).length,
);

// --- Example coverage ---
function exampleCoversTier(outcome: MockScoringOutcome): boolean {
  return formData.value.examples.some((e) => e.expectedOutcome === outcome);
}

const missingExampleTiers = computed(() =>
  formData.value.scoringTiers
    .filter((t) => !formData.value.examples.some((e) => e.expectedOutcome === t.outcome))
    .map((t) => t.outcome),
);

function tierPointsForOutcome(outcome: string): number {
  return formData.value.scoringTiers.find((t) => t.outcome === outcome)?.points ?? 0;
}

// --- Helpers ---

function methodSeverity(method: MockScoringMethod): "info" | "warn" | "success" {
  const map: Record<MockScoringMethod, "info" | "warn" | "success"> = {
    Deterministic: "success",
    LLM: "info",
    Hybrid: "warn",
  };
  return map[method];
}

function difficultyClass(difficulty: MockDifficulty) {
  const map: Record<MockDifficulty, string> = {
    Easy: "bg-green-100 text-green-700",
    Medium: "bg-amber-100 text-amber-700",
    Hard: "bg-red-100 text-red-700",
  };
  return map[difficulty] ?? "";
}

function statusClass(status: MockRuleStatus) {
  const map: Record<string, string> = {
    active: "bg-green-100 text-green-700",
    draft: "bg-amber-100 text-amber-700",
    archived: "bg-red-100 text-red-700",
  };
  return map[status] ?? "";
}

function statusDotClass(status: MockRuleStatus) {
  const map: Record<string, string> = {
    active: "bg-green-500",
    draft: "bg-amber-500",
    archived: "bg-red-500",
  };
  return map[status] ?? "";
}

function tierRowClass(outcome: MockScoringOutcome) {
  const map: Record<MockScoringOutcome, string> = {
    Pass: "bg-green-50",
    Partial: "bg-amber-50",
    Fail: "bg-red-50",
  };
  return map[outcome] ?? "";
}

function exampleBorderClass(outcome: string) {
  const map: Record<string, string> = {
    Pass: "border-green-300",
    Partial: "border-amber-300",
    Fail: "border-red-300",
  };
  return map[outcome] ?? "";
}

function outcomeSeverity(outcome: string): "success" | "warn" | "danger" | undefined {
  const map: Record<string, "success" | "warn" | "danger"> = {
    Pass: "success",
    Partial: "warn",
    Fail: "danger",
  };
  return map[outcome];
}

function formatMessage(content: string): string {
  return content
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\n/g, "<br />");
}

// --- Actions ---

function openCreate() {
  editingItem.value = null;
  formData.value = emptyForm();
  resetChat();
  viewMode.value = "edit";
}

function openEdit(item: MockRule) {
  editingItem.value = item;
  formData.value = JSON.parse(JSON.stringify(item));
  resetChat(item.name);
  viewMode.value = "edit";
}

function backToList() {
  viewMode.value = "list";
  editingItem.value = null;
}

function onSave() {
  if (editingItem.value) {
    update(editingItem.value.id, { ...formData.value, dateUpdated: new Date() });
  } else {
    add({ ...formData.value, id: faker.string.uuid(), dateCreated: new Date(), dateUpdated: new Date() });
  }
  viewMode.value = "list";
  editingItem.value = null;
}

function onDelete(item: MockRule) {
  confirm.require({
    header: "Delete Rule",
    message: `Are you sure you want to delete "${item.name}"?`,
    accept: () => remove(item.id),
  });
}

// --- Scoring guide ---

function clampPartialPoints(val: number): number {
  const max = formData.value.maxPoints - 1;
  return Math.max(1, Math.min(max, isNaN(val) ? 1 : val));
}

function addPartialTier() {
  if (hasPartialTier.value) return;
  const partialPoints = Math.max(1, Math.floor(formData.value.maxPoints / 2));
  const failIndex = formData.value.scoringTiers.findIndex((t) => t.outcome === "Fail");
  const insertAt = failIndex >= 0 ? failIndex : formData.value.scoringTiers.length;
  formData.value.scoringTiers.splice(insertAt, 0, {
    id: faker.string.uuid(),
    outcome: "Partial",
    points: partialPoints,
    description: "",
  });
}

function removePartialTier(index: number) {
  const tier = formData.value.scoringTiers[index];
  if (tier.outcome === "Partial") {
    formData.value.scoringTiers.splice(index, 1);
  }
}

// Keep Pass/Fail points in sync when maxPoints changes
watch(
  () => formData.value.maxPoints,
  (newMax) => {
    for (const tier of formData.value.scoringTiers) {
      if (tier.outcome === "Pass") {
        tier.points = newMax;
      } else if (tier.outcome === "Partial" && tier.points >= newMax) {
        tier.points = Math.max(1, newMax - 1);
      }
    }
  },
);

// --- Apply chat form patches ---
watch(lastFormPatch, (patch) => {
  if (!patch) return;
  const fd = formData.value;
  // Apply scalar / simple fields
  const scalarKeys: (keyof RuleFormPatch)[] = [
    "name", "statement", "category", "ruleType", "difficulty",
    "modelTier", "maxPoints", "additionalInstructions", "scoringMethod",
  ];
  for (const key of scalarKeys) {
    if (key in patch && patch[key] !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (fd as any)[key] = patch[key];
    }
  }
  // Replace arrays wholesale when present
  if (patch.scoringTiers) {
    fd.scoringTiers = patch.scoringTiers;
  }
  if (patch.examples) {
    fd.examples = patch.examples;
  }
  // Reset the patch so the same ref change triggers again if needed
  lastFormPatch.value = null;
});

// --- Examples ---

function addExample() {
  formData.value.examples.push({
    id: faker.string.uuid(),
    transcript: "",
    expectedOutcome: "",
    explanation: "",
  });
}

function addExampleForOutcome(outcome: string) {
  formData.value.examples.push({
    id: faker.string.uuid(),
    transcript: "",
    expectedOutcome: outcome,
    explanation: "",
  });
}

function removeExample(index: number) {
  formData.value.examples.splice(index, 1);
}

// --- Chat ---

async function handleSendMessage() {
  const text = chatInput.value.trim();
  if (!text || isTyping.value) return;
  chatInput.value = "";
  await sendMessage(text);
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
}

watch(messages, async () => {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
}, { deep: true });
</script>
